
import { Component, Getter, Action, Prop, mixins } from "nuxt-property-decorator"
import { extend, ValidationProvider } from "vee-validate"
import { required } from "vee-validate/dist/rules"
import Blocks from "~/mixins/blocks"
import {
  SET_DELIVERY_FLOOR,
  SET_DELIVERY_PROPERTY_TYPE,
  SET_LISTING,
  SET_DELIVERY_LIFT_AVAILABLE
} from "~/store/listing/actions"
import {
  GET_DELIVERY_FLOOR,
  GET_DELIVERY_LIFT_AVAILABLE,
  GET_DELIVERY_PROPERTY_TYPE
} from "~/store/listing/getters"
import InitialListing from "~/store/listing/state"
import { isFlat, isHouse } from "~/services/public/Listing"
import {
  FLOOR_GROUND,
  FLOORS,
  LIFT_AVAILABLE,
  LIFT_UNAVAILABLE,
  PROPERTY_TYPE_LIST_FLAT,
  PROPERTY_TYPE_LIST_HOUSE,
  PROPERTY_TYPE_LIST_MAIN
} from "~/constants/listing"
import {
  GET_FLOORS,
  GET_PROPERTY_TYPES,
  GET_PROPERTY_TYPES_MAIN,
  GET_PROPERTY_TYPES_FLAT,
  GET_PROPERTY_TYPES_HOUSE
} from "~/store/property/getters"
import { swapFirstTwoElements } from "~/scripts/useful-functions"

// Using ValidationProvider without validating anything is a workaround!
// ValidationProvider must be added as container for the main Sel items
// To allow subMenu Sel items using their own props
@Component({
  components: { ValidationProvider }
})
export default class PropertyDeliveryInput extends mixins(Blocks) {
  propertyTypesMain: any[] = PROPERTY_TYPE_LIST_MAIN
  propertyTypesHouse: any[] = PROPERTY_TYPE_LIST_HOUSE
  propertyTypesFlat: any[] = PROPERTY_TYPE_LIST_FLAT
  floors: any[] = FLOORS

  @Prop({ type: Boolean, default: false }) propertyTypeHidden: boolean
  @Prop({ type: Boolean, default: false }) propertyFloorHidden: boolean
  @Prop({ required: true, type: Object }) block: Record<string, any>
  @Prop({ default: false, type: Boolean }) virgin: boolean
  @Prop({ default: true, type: Boolean }) lazy: boolean
  @Prop({ default: false, type: Boolean }) validation: boolean

  @Action(SET_DELIVERY_PROPERTY_TYPE, { namespace: "listing" }) setDeliveryPropertyType: Function
  @Action(SET_DELIVERY_FLOOR, { namespace: "listing" }) setDeliveryFloor: Function
  @Action(SET_DELIVERY_LIFT_AVAILABLE, { namespace: "listing" }) setLiftAvailable: Function
  @Action(SET_LISTING, { namespace: "listing" }) setListing: Function

  @Getter(GET_DELIVERY_FLOOR, { namespace: "listing" }) getDeliveryFloor: string | null
  @Getter(GET_DELIVERY_LIFT_AVAILABLE, { namespace: "listing" })
  getDeliveryLiftAvailable: LiftAvailable

  @Getter(GET_PROPERTY_TYPES, { namespace: "property" })
  getStorePropertyTypes: ListingPropertyType[]

  @Getter(GET_PROPERTY_TYPES_MAIN, { namespace: "property" })
  getStorePropertyTypesMain: ListingPropertyType[]

  @Getter(GET_PROPERTY_TYPES_HOUSE, { namespace: "property" })
  getStorePropertyTypesHouse: ListingPropertyType[]

  @Getter(GET_PROPERTY_TYPES_FLAT, { namespace: "property" })
  getStorePropertyTypesFlat: ListingPropertyType[]

  @Getter(GET_FLOORS, { namespace: "property" }) getStoreFloors: ListingPropertyFloor[]
  @Getter(GET_DELIVERY_PROPERTY_TYPE, { namespace: "listing" }) getDeliveryPropertyType:
    | string
    | null

  created() {
    if (this.validation) {
      extend("required", required)
    }
    this.setListing(InitialListing())
  }

  get getPropertyTypesMain() {
    if (!this.getStorePropertyTypesMain) return this.propertyTypesMain

    const propertyTypesToShow = this.getStorePropertyTypesMain.filter(type => !type.hidden)

    if (this.$i18n.locale === "en") {
      return propertyTypesToShow
    }
    if (this.$i18n.locale === "es") {
      /* Spain-only experiment where we invert the order of house and flat to test a hypothesis */
      return swapFirstTwoElements(propertyTypesToShow)
    }
  }

  get getPropertyTypesHouse() {
    return this.getStorePropertyTypesHouse || this.propertyTypesHouse
  }

  get getPropertyTypesFlat() {
    return this.getStorePropertyTypesFlat || this.propertyTypesFlat
  }

  get getFloors() {
    return this.getStoreFloors || this.floors
  }

  get attributes() {
    return this.virgin ? null : this.Blocks_getAttributes(this.block)
  }

  get deliveryPropertyType(): string | null {
    return this.getDeliveryPropertyType
  }

  set deliveryPropertyType(value) {
    if (!this.isFlat(value) || this.getDeliveryFloor === FLOOR_GROUND) {
      this.setDeliveryFloor(FLOOR_GROUND)
      this.setLiftAvailable(null)
    }
    this.setDeliveryPropertyType(value)
  }

  get deliveryFloor(): string | null {
    return this.getDeliveryFloor
  }

  set deliveryFloor(value) {
    if (value === FLOOR_GROUND) {
      this.setLiftAvailable(null)
    }
    this.setDeliveryFloor(value)
  }

  get liftAvailable(): LiftAvailable {
    if (this.getDeliveryLiftAvailable === null) {
      this.setLiftAvailable(LIFT_AVAILABLE)
      return LIFT_AVAILABLE
    }
    return this.getDeliveryLiftAvailable
  }

  set liftAvailable(value) {
    this.setLiftAvailable(value)
  }

  get getLiftAvailableOptions(): any[] | null {
    return [
      {
        name: this.$t("location_form.lift_available"),
        value: LIFT_AVAILABLE
      },
      {
        name: this.$t("location_form.lift_not_available"),
        value: LIFT_UNAVAILABLE
      }
    ]
  }

  isHouse(propertyType) {
    return isHouse(propertyType)
  }

  isFlat(propertyType) {
    return isFlat(propertyType)
  }

  requiresFloor(propertyType) {
    if (!this.getStorePropertyTypes) {
      return this.propertyTypeHidden && !this.propertyFloorHidden
    }
    const propertyTypeObject: undefined | ListingPropertyType = this.getStorePropertyTypes.find(
      storePropertyType => storePropertyType.value === propertyType
    )
    return propertyTypeObject?.propertyWithLevels
  }
}
